import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
    format,
    addMonths,
    subMonths,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    isSameMonth,
    isSameDay,
    addDays,
    isAfter,
    isBefore,
    isWithinInterval,
    startOfDay,
    endOfDay,
} from "date-fns";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const DateBox = ({
    mode = "range",
    initialStartDate,
    initialEndDate,
    onApply,
    onClose,
    /** Omit or pass `undefined` = cap at end of today (default). Pass `null` = no upper limit. */
    maxDate,
    /** Optional; start of this day (inclusive). Dates before are disabled. */
    minDate = null,
}) => {
    const [startDate, setStartDate] = useState(initialStartDate ? startOfDay(new Date(initialStartDate)) : null);
    const [endDate, setEndDate] = useState(initialEndDate ? startOfDay(new Date(initialEndDate)) : null);
    const [currentMonth, setCurrentMonth] = useState(initialStartDate ? startOfMonth(new Date(initialStartDate)) : startOfMonth(new Date()));
    const [hoverDate, setHoverDate] = useState(null);
    const [menuStyle, setMenuStyle] = useState(null);
    const modalRef = useRef(null);
    const anchorRef = useRef(null);

    const updatePosition = () => {
        const parent = anchorRef.current?.parentElement;
        if (!parent) return;
        const rect = parent.getBoundingClientRect();
        const width =
            mode === "range"
                ? Math.min(Math.max(rect.width, 750), window.innerWidth - 24)
                : Math.min(Math.max(rect.width, 320), window.innerWidth - 24);
        let left = rect.left;
        if (left + width > window.innerWidth - 12) {
            left = Math.max(12, window.innerWidth - width - 12);
        }
        setMenuStyle({
            position: "fixed",
            top: rect.bottom + 8,
            left,
            width,
            zIndex: 9999,
        });
    };

    useLayoutEffect(() => {
        updatePosition();
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);
        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [mode]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);
    const normalizedMaxDate =
        maxDate === null
            ? endOfDay(addMonths(new Date(), 120))
            : maxDate === undefined
              ? endOfDay(new Date())
              : endOfDay(new Date(maxDate));

    const normalizedMinDate =
        minDate != null && minDate !== undefined
            ? startOfDay(new Date(minDate))
            : null;

    const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
    const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

    const handleDateClick = (day) => {
        const clickedDay = startOfDay(day);
        if (isAfter(clickedDay, normalizedMaxDate)) return;
        if (normalizedMinDate && isBefore(clickedDay, normalizedMinDate)) return;

        if (mode === "single") {
            setStartDate(clickedDay);
            onApply(clickedDay);
            return;
        }

        if (!startDate || (startDate && endDate)) {
            setStartDate(clickedDay);
            setEndDate(null);
        } else if (startDate && !endDate) {
            if (isBefore(clickedDay, startDate)) {
                setStartDate(clickedDay);
                setEndDate(null);
            } else {
                setEndDate(clickedDay);
                onApply([startDate, clickedDay]);
            }
        }
    };

    const renderHeader = (month, showPrev = true, showNext = true) => {
        return (
            <div className={`flex items-center justify-between border-b border-gray-100 dark:border-white/5 relative ${mode === 'single' ? 'px-4 py-3' : 'px-6 py-4'}`}>
                <button
                    onClick={prevMonth}
                    className={`p-2 hover:bg-gray-100 dark:hover:bg-white/5 rounded-full transition-colors ${!showPrev ? 'invisible' : ''}`}
                >
                    <ChevronLeft className="w-5 h-5 text-gray-500" />
                </button>
                <h2 className="text-sm font-extrabold text-gray-900 dark:text-white uppercase tracking-widest text-center">
                    {format(month, "MMMM yyyy")}
                </h2>
                <button
                    onClick={nextMonth}
                    className={`p-2 hover:bg-gray-100 dark:hover:bg-white/5 rounded-full transition-colors ${!showNext ? 'invisible' : ''}`}
                >
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                </button>
            </div>
        );
    };

    const renderDays = () => {
        const days = ["M", "T", "W", "T", "F", "S", "S"];
        return (
            <div className="grid grid-cols-7 mb-2">
                {days.map((day, i) => (
                    <div key={i} className="text-center text-[11px] font-black text-gray-400 dark:text-gray-600 uppercase py-2">
                        {day}
                    </div>
                ))}
            </div>
        );
    };

    const renderCells = (month) => {
        const monthStart = startOfMonth(month);
        const monthEnd = endOfMonth(monthStart);
        const startDateView = startOfWeek(monthStart, { weekStartsOn: 1 });
        const endDateView = endOfWeek(monthEnd, { weekStartsOn: 1 });

        const rows = [];
        let days = [];
        let day = startDateView;

        while (day <= endDateView) {
            for (let i = 0; i < 7; i++) {
                const cloneDay = startOfDay(day);
                const isOutsideMonth = !isSameMonth(cloneDay, monthStart);
                const isAfterMax = isAfter(cloneDay, normalizedMaxDate);
                const isBeforeMin =
                    normalizedMinDate && isBefore(cloneDay, normalizedMinDate);
                const isDisabled = isOutsideMonth || isAfterMax || isBeforeMin;

                const isStart = startDate && isSameDay(cloneDay, startDate);
                const isEnd = endDate && isSameDay(cloneDay, endDate);
                const isSelected = isStart || isEnd;

                let isRange = false;
                if (mode === "range") {
                    if (startDate && endDate) {
                        isRange = isWithinInterval(cloneDay, { start: startDate, end: endDate });
                    } else if (startDate && hoverDate && !isBefore(hoverDate, startDate)) {
                        isRange = isWithinInterval(cloneDay, { start: startDate, end: hoverDate });
                    }
                }

                days.push(
                    <div
                        key={cloneDay.toString()}
                        className={`relative h-11 flex items-center justify-center cursor-pointer transition-all group
              ${isOutsideMonth ? "opacity-0 pointer-events-none" : ""}
              ${isAfterMax || isBeforeMin ? "opacity-20 cursor-not-allowed" : "hover:z-20"}
            `}
                        onClick={() => handleDateClick(cloneDay)}
                        onMouseEnter={() => mode === "range" && startDate && !endDate && setHoverDate(cloneDay)}
                    >
                        {/* Range Background (Rounded Squares for inner days) */}
                        {isRange && !isSelected && (
                            <div className="absolute inset-0.5 z-0 bg-gray-50 dark:bg-white/5 rounded-xl border border-gray-100 dark:border-white/5" />
                        )}

                        {/* Selection Background (Rounded Square with Glow) */}
                        <div className={`
              w-10 h-10 flex items-center justify-center text-sm font-bold rounded-xl z-10 transition-all duration-300
              ${isSelected ? "bg-teal-600 text-white shadow-[0_0_20px_rgba(20,184,166,0.3)] scale-105" : "text-gray-700 dark:text-gray-400"}
              ${isRange && !isSelected ? "text-gray-900 dark:text-white bg-gray-100 dark:bg-white/10" : ""}
              ${!isRange && !isSelected && !isAfterMax && !isBeforeMin ? "hover:bg-gray-100 dark:hover:bg-white/5" : ""}
            `}>
                            {format(cloneDay, "d")}
                        </div>
                    </div>
                );
                day = addDays(day, 1);
            }
            rows.push(
                <div className="grid grid-cols-7 gap-1" key={day.toString()}>
                    {days}
                </div>
            );
            days = [];
        }
        return <div className={`pb-6 ${mode === 'single' ? 'px-2' : 'px-5'}`}>{rows}</div>;
    };

    return (
        <>
            <span ref={anchorRef} className="absolute top-full left-0 w-0 h-0" aria-hidden="true" />
            {menuStyle &&
                createPortal(
                    <motion.div
                        ref={modalRef}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        style={menuStyle}
                        className="bg-white dark:bg-[#111111] rounded-[1.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.15)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.4)] overflow-hidden border border-gray-100 dark:border-white/5"
                    >
                        <div className={`flex flex-col ${mode === "range" ? "md:flex-row" : "max-w-md mx-auto"}`}>
                            <div className="flex-1">
                                {renderHeader(currentMonth, true, mode === "single")}
                                <div className="p-2">
                                    {renderDays()}
                                    {renderCells(currentMonth)}
                                </div>
                            </div>
                            {mode === "range" && (
                                <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-100 dark:border-white/5">
                                    {renderHeader(addMonths(currentMonth, 1), false, true)}
                                    <div className="p-2">
                                        {renderDays()}
                                        {renderCells(addMonths(currentMonth, 1))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>,
                    document.body
                )}
        </>
    );
};

export default DateBox;
